import { z } from "zod";

// Viewer diagnostics are untrusted. Remove URLs and credentials before sending bounded fields to telemetry.
export function replayDiagnosticText({
  value,
  limit,
}: Readonly<{
  value: unknown;
  limit: number;
}>): string | undefined {
  if (typeof value !== "string") return undefined;

  return value
    .replace(/(?:https?|blob|file):[^\s<>"']+/gi, "[url]")
    .replace(/\S+/g, (fragment) =>
      /%[0-9a-f]{2}/i.test(fragment) ? "[redacted]" : fragment,
    )
    .replace(/Bearer\s+[^\s,;]+/gi, "Bearer [redacted]")
    .replace(
      /(?:token|grant|signature|credential|authorization|password|policy|key-pair-id)["']?\s*[=:]\s*["']?[^\s,;"'}]+/gi,
      "[redacted]",
    )
    .replace(/eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g, "[redacted]")
    .slice(0, limit);
}

function replayDiagnosticNumber(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) && value >= 0
    ? value
    : undefined;
}

export function parseReplayDiagnostics(
  value: unknown,
): Readonly<Record<string, string | number | undefined>> | undefined {
  const parsed = z.record(z.string(), z.unknown()).safeParse(value);

  if (!parsed.success) return undefined;

  const data = parsed.data;

  return {
    viewer_error_name: replayDiagnosticText({ value: data.name, limit: 80 }),
    viewer_error_message: replayDiagnosticText({
      value: data.message,
      limit: 1024,
    }),
    viewer_error_stack: replayDiagnosticText({
      value: data.stack,
      limit: 4096,
    }),
    viewer_error_stage: replayDiagnosticText({ value: data.stage, limit: 80 }),
    viewer_error_phase: replayDiagnosticText({ value: data.phase, limit: 80 }),
    viewer_error_wasm_heap_bytes: replayDiagnosticNumber(data.wasmHeapBytes),
    viewer_error_asyncify_state: replayDiagnosticNumber(data.asyncifyState),
    viewer_error_elapsed_ms: replayDiagnosticNumber(data.elapsedMs),
    viewer_error_memory_grow_failure_count: replayDiagnosticNumber(
      data.memoryGrowFailureCount,
    ),
    viewer_error_memory_grow_before_bytes: replayDiagnosticNumber(
      data.memoryGrowBeforeBytes,
    ),
    viewer_error_memory_grow_requested_bytes: replayDiagnosticNumber(
      data.memoryGrowRequestedBytes,
    ),
    viewer_error_visibility:
      data.visibility === "visible" || data.visibility === "hidden"
        ? data.visibility
        : undefined,
  };
}
